import {
  Box,
  Chip,
  Divider,
  Grid,
  GridColumn,
  Text
} from "@modernatx/ui-kit-react";
import React from "react";

import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { Invoice, InvoiceDiscount, InvoiceStatus } from "@/types/Invoice";
import { formatCurrency, formatTimestampDate } from "@/utils/InvoicePay";

export interface InvoicePriceSummaryProps {
  invoice: Invoice;
  promptPayDiscount?: InvoiceDiscount;
}

export const InvoicePriceSummary: React.FC<InvoicePriceSummaryProps> = ({
  invoice,
  promptPayDiscount
}) => {
  const onlineDiscountEnabled = useFeatureFlag("ONLINE_DISCOUNT");

  const totalDoses = onlineDiscountEnabled
    ? invoice.orderLineItems.reduce(
        (accumulator, currentValue) =>
          accumulator + Number(currentValue.quantity),
        0
      )
    : undefined;

  const subTotalHeader =
    onlineDiscountEnabled && totalDoses
      ? `Subtotal (${Number(totalDoses)} doses):`
      : "Subtotal:";

  return (
    <>
      <Grid sx={{ pt: "spacing-9", pb: "spacing-6" }}>
        <GridColumn push={[0, 4, 8]}>
          <Grid>
            <GridColumn size={3} sx={{ alignItems: "end" }}>
              <Text variant="body-1-medium">{subTotalHeader}</Text>
              {/* 
                      todo: MD-348 to add online discount
                      <Text variant="body-1-regular">Online discount (2%):</Text> 
                      */}
            </GridColumn>
            <GridColumn size={1} fillGapStart={true} sx={{ alignItems: "end" }}>
              <Text variant="body-1-medium">
                {formatCurrency(invoice.subtotal)}
              </Text>
            </GridColumn>
          </Grid>
        </GridColumn>

        <GridColumn push={[0, 4, 8]}>
          <Grid>
            <GridColumn size={3} sx={{ alignItems: "end" }}>
              {/* 
                      todo: MD-348 to add online discount
                      <Text variant="body-1-medium">New subtotal:</Text> 
                      */}
              <Text variant="body-1-regular">Shipping:</Text>
            </GridColumn>
            <GridColumn size={1} fillGapStart={true} sx={{ alignItems: "end" }}>
              {/*  shipping should always be 0.00 for now */}
              <Text variant="body-1-regular">{formatCurrency(0)}</Text>
            </GridColumn>
          </Grid>
        </GridColumn>
        <GridColumn push={[0, 4, 8]}>
          <Grid>
            <GridColumn size={3} sx={{ alignItems: "end" }}>
              {/* 
                      todo: MD-348 to add online discount
                      <Text variant="body-1-medium">New subtotal:</Text> 
                      */}
              <Text variant="body-1-regular">Sales tax:</Text>
            </GridColumn>
            <GridColumn size={1} fillGapStart={true} sx={{ alignItems: "end" }}>
              {/* 
                      todo: MD-348 to add online discount
                      <Text variant="body-1-medium">
                        $
                        {formatCurrency(
                          invoice.subtotal - (onlineDiscount?.discountAmount ?? 0)
                        )}
                      </Text> */}
              <Text variant="body-1-regular">
                {formatCurrency(invoice.taxes ?? 0)}
              </Text>
            </GridColumn>
          </Grid>
        </GridColumn>

        <GridColumn size={2} push={[2, 6, 10]}>
          <Box sx={{ display: "inline-block", py: "spacing-3" }}>
            <Divider color="color-system-gray-100" />
          </Box>
        </GridColumn>
        {/* Invoice total */}
        <GridColumn push={[0, 4, 8]}>
          <Grid>
            <GridColumn size={3} sx={{ alignItems: "end" }}>
              <Text variant="body-1-medium">Invoice total:</Text>
            </GridColumn>
            <GridColumn size={1} fillGapStart={true} sx={{ alignItems: "end" }}>
              <Text variant="body-1-medium">
                {formatCurrency(invoice.subtotal + invoice.taxes)}
              </Text>
            </GridColumn>
          </Grid>
        </GridColumn>
        {/* Prompt pay discount */}

        <GridColumn push={[0, 4, 8]}>
          {
            <Grid>
              <GridColumn size={3} sx={{ alignItems: "end" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "spacing-2"
                  }}
                >
                  {invoice.status === InvoiceStatus.Overdue && (
                    <Chip variant="warning" size="medium" label="Expired" />
                  )}
                  <Text variant="body-1-regular">Prompt pay discount:*</Text>
                </Box>
              </GridColumn>
              <GridColumn
                size={1}
                fillGapStart={true}
                sx={{ alignItems: "end" }}
              >
                <Text variant="body-1-regular">
                  -{formatCurrency(promptPayDiscount?.discountAmount ?? 0)}
                </Text>
              </GridColumn>
            </Grid>
          }
        </GridColumn>
      </Grid>

      <Grid>
        <GridColumn size={5} push={[0, 4, 7]}>
          <Box sx={{ display: "inline-block", pb: "spacing-6" }}>
            <Divider color="color-current-brand-600" />
          </Box>
        </GridColumn>
        <GridColumn push={[0, 2, 4]}>
          <Grid>
            <GridColumn
              size={[2, 4, 6]}
              fillGapEnd={true}
              sx={{ alignItems: "end", color: "color-current-brand-800" }}
            >
              {promptPayDiscount ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end"
                  }}
                >
                  <Text variant="h5">
                    Total due if paid by
                    <Text sx={{ mx: "spacing-2" }}>
                      {formatTimestampDate(
                        promptPayDiscount?.discountExpiration
                      )}
                    </Text>
                    :
                  </Text>
                  <Text variant="body-1-medium">
                    If paid after
                    <Text sx={{ mx: "spacing-2" }}>
                      {formatTimestampDate(invoice.dueDate)}
                    </Text>
                    :
                  </Text>
                </Box>
              ) : (
                <Text variant="h5">Total amount due:</Text>
              )}
            </GridColumn>
            <GridColumn
              size={2}
              sx={{ alignItems: "end", color: "color-current-brand-800" }}
            >
              {promptPayDiscount ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end"
                  }}
                >
                  <Text variant="h5">{formatCurrency(invoice.total)}</Text>
                  <Text variant="body-1-medium">
                    {formatCurrency(invoice.subtotal + invoice.taxes)}
                  </Text>
                </Box>
              ) : (
                <Text variant="h5">
                  {formatCurrency(invoice.subtotal + invoice.taxes)}
                </Text>
              )}
            </GridColumn>
          </Grid>
        </GridColumn>
        <GridColumn size={5} push={[0, 4, 7]}>
          <Box sx={{ display: "inline-block", pt: "spacing-6" }}>
            <Divider color="color-current-brand-600" />
          </Box>
        </GridColumn>
        <GridColumn size={5} push={[0, 4, 7]}>
          <Text
            variant="body-2-regular"
            sx={{
              paddingTop: "spacing-5",
              paddingBottom: "spacing-5",
              color: "text-secondary"
            }}
          >
            *Your contract terms entitle you to a 2% discount if orders are paid
            within 60 days of your invoice date.
          </Text>
        </GridColumn>
      </Grid>
    </>
  );
};
